import { useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";
import Modal from "../../../components/Modal";
import CloseButton from "../../../components/CloseButton";

// AI 첨삭 고치기(2026-10-17). 리더스북 쪽 위 답 칸과 원서 워크북 서술형을 AI 가 매기고 첨삭한 것을
// 선생님이 바꾼다. 서버: api/assignment/correction_views.py
// 고친 문장을 적으면 서버가 아이 글과 낱말 단위로 견주어 빨간 펜(줄 긋기 · 고친 말)을 다시 만들고,
// 점수를 다시 매기고, 해설지를 새로 굽게 한다.

const VERDICT_LIST = [
  { value: "ok", label: "○", color: "#1DB05B" },
  { value: "half", label: "△", color: "#E8920A" },
  { value: "no", label: "✕", color: "#E0312B" },
];

const SegmentPreview = ({ segmentList, text }) => {
  if (!segmentList || !segmentList.length) return <span>{text}</span>;
  return (
    <span>
      {segmentList.map((segment, index) => {
        if (segment.kind === "wrong") {
          return (
            <span key={index}>
              <s style={{ color: "#8A949C" }}>{segment.text}</s>
              {segment.fix && <span style={{ color: "#E0312B", fontWeight: 700 }}> {segment.fix}</span>}
            </span>
          );
        }
        if (segment.kind === "insert") {
          return (
            <span key={index} style={{ color: "#E0312B", fontWeight: 700 }}>
              {" "}∧{segment.fix}{" "}
            </span>
          );
        }
        return <span key={index}>{segment.text}</span>;
      })}
    </span>
  );
};

const CorrectionItem = ({ assignmentId, item, onSaved }) => {
  const [verdict, setVerdict] = useState(item.verdict || "half");
  const [correctedText, setCorrectedText] = useState(item.corrected_text || "");
  const [reason, setReason] = useState(item.reason || "");
  const [isSaving, setIsSaving] = useState(false);
  const isChanged =
    verdict !== item.verdict || correctedText !== (item.corrected_text || "") || reason !== (item.reason || "");

  const save = async () => {
    setIsSaving(true);
    try {
      await apiClient.post(
        "/api/v1/correction/update",
        {
          assignment_id: assignmentId,
          section_id: item.section_id,
          key: item.key,
          verdict,
          corrected_text: correctedText,
          reason,
        },
        { withAuth: true }
      );
      onSaved();
    } catch (error) {
      alert(error?.response?.data?.error || "저장하지 못했어요.");
    } finally {
      setIsSaving(false);
    }
  };

  const fieldStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    border: "1px solid #E6EDEC",
    borderRadius: "10px",
    fontFamily: "inherit",
    fontSize: "15px",
    resize: "vertical",
  };

  return (
    <div style={{ padding: "14px 0", borderBottom: "1px solid #EEF2F3" }}>
      <div style={{ fontSize: "12px", fontWeight: 800, color: "#8A949C" }}>
        {item.where}
        {item.is_teacher_edited && <span style={{ marginLeft: "6px", color: "#00A396" }}>선생님이 고침</span>}
      </div>
      {item.prompt && <div style={{ marginTop: "4px", fontSize: "14px", color: "#3A434E" }}>{item.prompt}</div>}
      <div style={{ marginTop: "8px", padding: "10px 12px", borderRadius: "10px", background: "#FFFBEA", fontSize: "15px", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
        <SegmentPreview segmentList={item.segments} text={item.text || "(안 씀)"} />
      </div>
      {item.answer && (
        <div style={{ marginTop: "6px", fontSize: "13px", color: "#5B666F", whiteSpace: "pre-wrap" }}>
          <b>모범 답</b> {item.answer}
        </div>
      )}
      <div style={{ display: "flex", gap: "6px", marginTop: "10px" }}>
        {VERDICT_LIST.map((one) => (
          <button
            key={one.value}
            type="button"
            onClick={() => setVerdict(one.value)}
            style={{
              width: "44px",
              height: "36px",
              borderRadius: "10px",
              border: `1.5px solid ${one.color}`,
              background: verdict === one.value ? one.color : "#fff",
              color: verdict === one.value ? "#fff" : one.color,
              fontSize: "18px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            {one.label}
          </button>
        ))}
      </div>
      {verdict !== "ok" && (
        <textarea
          value={correctedText}
          onChange={(event) => setCorrectedText(event.target.value)}
          placeholder="고친 문장"
          rows={2}
          style={{ ...fieldStyle, marginTop: "8px" }}
        />
      )}
      <textarea
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="고친 이유"
        rows={2}
        maxLength={600}
        style={{ ...fieldStyle, marginTop: "8px" }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
        <button
          type="button"
          disabled={!isChanged || isSaving}
          onClick={save}
          style={{
            padding: "8px 20px",
            borderRadius: "999px",
            border: "none",
            background: isChanged ? "#0E141B" : "#E6EDEC",
            color: isChanged ? "#fff" : "#8A949C",
            fontWeight: 800,
            fontFamily: "inherit",
            cursor: isChanged ? "pointer" : "default",
          }}
        >
          {isSaving ? "저장 중..." : "저장"}
        </button>
      </div>
    </div>
  );
};

const CorrectionModal = ({ assignmentId, onClose }) => {
  const [data, setData] = useState(null);

  const load = () =>
    apiClient
      .get("/api/v1/correction/list", { params: { assignment_id: assignmentId }, withAuth: true })
      .then((response) => setData(response.data))
      .catch(() => setData({ error: true }));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  return (
    <Modal>
      <div
        style={{
          position: "relative",
          width: "min(760px, calc(100vw - 40px))",
          maxHeight: "86vh",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          borderRadius: "18px",
          overflow: "hidden",
        }}
      >
        <CloseButton onClick={onClose} />
        <div style={{ padding: "22px 22px 6px" }}>
          <div className="ws-modal-title">{data?.student_name} 첨삭 고치기</div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 22px 16px" }}>
          {!data && <p className="ws-modal-sub">불러오는 중입니다.</p>}
          {data?.error && <p className="ws-modal-sub">불러오지 못했습니다.</p>}
          {data && !data.error && data.items.length === 0 && <p className="ws-modal-sub">AI 가 첨삭한 답이 없어요.</p>}
          {(data?.items || []).map((item) => (
            <CorrectionItem key={`${item.section_id}-${item.key}-${item.is_teacher_edited}`} assignmentId={assignmentId} item={item} onSaved={load} />
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default CorrectionModal;
